import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, signal } from '@angular/core';

import { IconComponent } from '../../../icon/icon.component';
import { CoursePlayerHeaderComponent } from '../../../course-player-header/course-player-header.component';
import { CourseNavigationItem, PlayerMode } from '../../../../../services/course-player.service';
import { UnitPassSummaryComponent } from '../../../../passing-policy/unit-pass-summary.component';
import { UnitResultView } from '../../../../passing-policy/passing-policy-results.service';
import { ClampOverflowDirective } from '../../../../directives/clamp-overflow.directive';

@Component({
  selector: 'app-learner-unit-intro',
  standalone: true,
  imports: [IconComponent, CoursePlayerHeaderComponent, UnitPassSummaryComponent, ClampOverflowDirective],
  templateUrl: './unit-intro.component.html',
  styleUrls: ['./unit-intro.component.scss']
})
export class UnitIntroComponent implements OnChanges {
  @Input() currentItem!: CourseNavigationItem;
  @Input() currentItemIndex = 0;
  @Input() totalItems = 1;
  @Input() canGoToPrevious = false;
  @Input() canGoToNext = false;
  @Input() isMobile = false;
  @Input() showTimeline = true;
  @Input() isFullscreen = false;
  /** KAN-620: false where the browser cannot fullscreen the player (iPhone). */
  @Input() showFullscreen = true;
  @Input() playerMode: PlayerMode = 'learner';
  @Input() unitStats: { totalModules: number; hasUnitQuiz: boolean } | null = null;
  // KAN-326: when set, the unit is locked (completion-order gate or KAN-318 pass
  // gate) and this reason is shown INSIDE the overview card next to the disabled
  // "Continue to First Module" button, so the learner sees a clear explanation.
  @Input() lockReason: string | null = null;
  // KAN-370: the unit's live pass/fail verdict. Rendered INSIDE the overview card
  // (was a clipped sibling below this full-height component). The pass-summary
  // self-guards (renders nothing when null or 'not_applicable').
  @Input() unitResult: UnitResultView | null = null;

  @Output() onNavigatePrevious = new EventEmitter<void>();
  @Output() onNavigateNext = new EventEmitter<void>();
  @Output() onTimelineToggle = new EventEmitter<void>();
  @Output() onFullscreenToggle = new EventEmitter<void>();
  @Output() onCloseModal = new EventEmitter<void>();

  // KAN-676: the description is clamped to 6 lines; when the clamp actually hides
  // text (measured, so it follows the viewport width and phone rotation) a
  // Show more / Show less toggle reveals the rest. Signals because the measurement
  // arrives from a ResizeObserver, outside any template event (zoneless app).
  readonly descriptionClamped = signal(false);
  readonly descriptionExpanded = signal(false);

  ngOnChanges(changes: SimpleChanges): void {
    const item = changes['currentItem'];
    if (item && !item.firstChange && item.previousValue?.id !== item.currentValue?.id) {
      this.descriptionExpanded.set(false);
    }
  }

  onDescriptionClampChange(clamped: boolean): void {
    // Expanded, the paragraph is unclamped and always "fits"; keep the last
    // collapsed reading so the Show less control stays put.
    if (!this.descriptionExpanded()) {
      this.descriptionClamped.set(clamped);
    }
  }

  toggleDescription(): void {
    this.descriptionExpanded.update((expanded) => !expanded);
  }
}
